import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { usePortalLanguage } from '../hooks/usePortalLanguage';
import { fetchPortalBranding, portalLogoUrl } from '../services/portalBranding';

export default function PortalLogin() {
  const navigate = useNavigate();
  const { language, t, toggleLanguage } = usePortalLanguage();
  const [form, setForm] = useState({ login: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState({ brandName: null, hasLogo: false });

  useEffect(() => { fetchPortalBranding().then(setBranding); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/portal/login', form);
      navigate('/portal/dashboard');
    } catch {
      setError(t('login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={toggleLanguage}
            className="inline-flex h-8 items-center rounded-lg border border-gray-200 bg-white px-3 text-xs font-bold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            title={language === 'en' ? 'Переключить на русский' : 'Switch to English'}
          >
            {language === 'en' ? 'RU' : 'EN'}
          </button>
        </div>
        {/* Logo / firm name */}
        <div className="text-center mb-8">
          {branding.hasLogo ? (
            <img src={portalLogoUrl()} alt={branding.brandName || t('login.title')} className="mx-auto mb-4 h-14 max-w-[220px] object-contain" />
          ) : (
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-600 mb-4 shadow-lg">
              <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-white" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l9-3 9 3v6c0 5.25-3.75 9-9 10.5C6.75 21 3 17.25 3 12V6z" />
              </svg>
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{branding.brandName || t('login.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('login.subtitle')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('login.loginLabel')}</label>
              <input
                type="text"
                required
                value={form.login}
                onChange={e => setForm(p => ({ ...p, login: e.target.value }))}
                placeholder="your@email.com"
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('login.passwordLabel')}</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="••••••••••"
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors mt-2"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          {t('login.needHelp')}
        </p>
      </div>
    </div>
  );
}
